import PropTypes from 'prop-types'
import React, { Component } from 'react'
import ImmutablePropTypes from 'react-immutable-proptypes'
import ReactAvatarEditor from 'react-avatar-editor'
import ImageCompression from 'browser-image-compression'
import Dropzone from 'react-dropzone'
import { connect as connectRedux } from 'react-redux'
import compose from 'recompose/compose'
import Button, { TYPES } from '../Button/index'
import Sherpa, { TYPE_SMALL_BLUE } from '../Sherpa/index'
import { getBoundActions } from '../../actions/index'
import { get as getConfig } from '../../config/index'
import { connect as connectStaticText } from '../StaticText/connect'
import { H2 } from '../Heading/index'
import IconV2, { ICON_TYPES } from '../Icon.v2/index'
import { isBrowser } from '../../config/environment'
import { posthog, POSTHOG_FEATURE_FLAGS } from '../PostHog/index'

const AVATAR_IMAGE_SIZE = 28
const avatarConfig = getConfig()

function isCommunityProfileEnabled () {
  if (!avatarConfig?.features?.communityProfile) return false
  if (!avatarConfig?.features?.posthog) return true
  if (!isBrowser()) return false
  return !!posthog.getFeatureFlag(
    POSTHOG_FEATURE_FLAGS.COMMUNITY_EMPLOYEE_TEST,
  )
}

function logCallback () {
  return null
}

class AvatarUpload extends Component {
  state = {
    image: null,
    allowZoomOut: false,
    position: { x: 0.5, y: 0.5 },
    scale: 1,
    rotate: 0,
    borderRadius: 100,
    preview: null,
    resizeError: null,
  }

  componentDidUpdate (prevProps) {
    const { communityProfile: prevCp, user: prevUser } = prevProps
    const {
      communityProfile: nextCp,
      user: nextUser,
      setOverlayDialogVisible,
    } = this.props

    // Close dialog on GraphQL avatar upload success
    const prevUploadSuccess = prevCp.getIn(['avatarUpload', 'success'])
    const nextUploadSuccess = nextCp.getIn(['avatarUpload', 'success'])
    if (nextUploadSuccess && !prevUploadSuccess) {
      setOverlayDialogVisible(null, false)
    }

    // Legacy fallback: close dialog on REST upload success
    const nextProfileImagesSuccess = nextUser.getIn(['userProfileImages', 'success'])
    const previousProfileImagesSuccess = prevUser.getIn(['userProfileImages', 'success'])
    if (nextProfileImagesSuccess !== previousProfileImagesSuccess) {
      setOverlayDialogVisible(null, false)
    }
  }

  componentWillUnmount () {
    const { clearUserProfileData } = this.props
    clearUserProfileData()
  }

  onClickUploadError = () => {
    this.setState({ uploadError: false })
  }

  onClickClose = (e) => {
    const { setOverlayDialogVisible } = this.props
    e.preventDefault()
    setOverlayDialogVisible(null, false)
  }

  onClickChooseFile = (e) => {
    e.preventDefault()
    if (this.fileUpload) {
      this.fileUpload.click()
    }
  }

  setEditorRef = (editor) => {
    if (editor) {
      this.editor = editor
    }
  }

  setChooseFileRef = (fileUpload) => {
    if (fileUpload) {
      this.fileUpload = fileUpload
    }
  }

  handleSave = async () => {
    try {
      if (!this.editor) {
        return
      }
      const canvas = this.editor.getImageScaledToCanvas()
      const rect = this.editor.getCroppingRect()

      if (isCommunityProfileEnabled()) {
        const { uploadAvatar } = this.props
        const profileDataUrl = canvas.toDataURL('image/png')

        const imageBlob = await new Promise((resolve, reject) => {
          canvas.toBlob((blob) => {
            if (blob) resolve(blob)
            else reject(new Error('Canvas toBlob returned null'))
          }, 'image/png')
        })

        uploadAvatar({
          imageBlob,
          fileExtension: 'png',
        })

        this.setState({
          preview: {
            img: profileDataUrl,
            rect,
            scale: this.state.scale,
            width: this.state.width,
            height: this.state.height,
            borderRadius: this.state.borderRadius,
          },
        })
      } else {
        const { updateUserProfileImages, auth } = this.props
        const profile = canvas.toDataURL()
        const formattedProfileImage = await ImageCompression.getFilefromDataUrl(profile)
        const avatarImage = await ImageCompression(formattedProfileImage, {
          maxWidthOrHeight: AVATAR_IMAGE_SIZE,
        })
        const fileReader = new FileReader()
        fileReader.onload = (fileEvent) => {
          const avatar = fileEvent.target.result
          updateUserProfileImages({ profile, avatar, auth })
        }
        fileReader.readAsDataURL(avatarImage)
        this.setState({
          preview: {
            img: profile,
            rect,
            scale: this.state.scale,
            width: this.state.width,
            height: this.state.height,
            borderRadius: this.state.borderRadius,
          },
        })
      }
    } catch (e) {
      this.setState({ resizeError: e })
    }
  }

  handleNewImage = (e) => {
    const FileSize = e.target.files[0].size / 1024 / 1024 // in MB
    if (FileSize > 2) {
      this.setState({ uploadError: true })
    } else {
      this.setState({ image: e.target.files[0] })
    }
  }

  handlePositionChange = (position) => {
    this.setState({ position })
  }

  handleDrop = (acceptedFiles) => {
    const FileSize = acceptedFiles[0].size / 1024 / 1024 // in MB
    if (FileSize > 2) {
      this.setState({ uploadError: true })
    } else {
      this.setState({ image: acceptedFiles[0] })
    }
  }

  handleScale = (e) => {
    const scale = parseFloat(e.target.value)
    this.setState({ scale })
  }

  render () {
    const { staticText, user, communityProfile } = this.props
    const { resizeError } = this.state
    const processing = user.get('userProfileImagesProcessing')
      || communityProfile.getIn(['avatarUpload', 'processing'])

    if (this.state.uploadError) {
      return (
        <div className="avatar-upload__error-container">
          <p>{staticText.getIn(['data', 'sorry'])}</p>
          <p>{staticText.getIn(['data', 'pleaseTryAgain'])}</p>
          <Button
            type={TYPES.PRIMARY}
            className="avatar-upload__button-try"
            onClick={() => this.onClickUploadError()}
          >
            {staticText.getIn(['data', 'tryAgain'])}
          </Button>
        </div>
      )
    }

    return (
      <div className="avatar-upload">
        {
          processing
            ? <div className="avatar-upload__processing">
              <Sherpa className={['avatar-upload__sherpa']} type={TYPE_SMALL_BLUE} />
              <div className="avatar-upload__processing-text">
                {`${staticText.getIn(['data', 'savingImage'])}...`}
              </div>
            </div>
            : null
        }
        <H2 className="avatar-upload__title" fixed>{staticText.getIn(['data', 'position'])}</H2>
        {
          resizeError
            ? <div className="avatar-upload__error">
              {staticText.getIn(['data', 'resizeError'])}
            </div>
            : null
        }
        <Dropzone
          onDrop={this.handleDrop}
          disableClick
          multiple={false}
          style={{ width: this.state.width, height: this.state.height, marginBottom: '15px' }}
        >
          <div className="avatar-upload__editor">
            <ReactAvatarEditor
              ref={this.setEditorRef}
              scale={parseFloat(this.state.scale)}
              width={this.state.width}
              height={this.state.height}
              position={this.state.position}
              onPositionChange={this.handlePositionChange}
              rotate={parseFloat(this.state.rotate)}
              borderRadius={this.state.borderRadius}
              /* eslint-disable react/jsx-no-bind */
              onLoadFailure={logCallback.bind(this, 'onLoadFailed')}
              onLoadSuccess={logCallback.bind(this, 'onLoadSuccess')}
              onImageReady={logCallback.bind(this, 'onImageReady')}
              onImageLoad={logCallback.bind(this, 'onImageLoad')}
              image={this.state.image}
              /* eslint-enable react/jsx-no-bind */
            />
          </div>
        </Dropzone>
        <Button
          type={TYPES.PRIMARY}
          className="avatar-upload__inputFile-button"
          onClick={(e) => this.onClickChooseFile(e)}
        >
          {staticText.getIn(['data', 'choose'])}
        </Button>
        <p className="avatar-upload__inputFile-sub-label">{staticText.getIn(['data', 'maxSize'])}</p>
        <input
          className="avatar-upload__inputFile"
          name="newImage"
          type="file"
          id="newImage"
          accept="image/*"
          onChange={this.handleNewImage}
          ref={this.setChooseFileRef}
        />
        <div className="avatar-upload__zoom-container">
          <IconV2
            className="avatar-upload__icon"
            type={ICON_TYPES.MINUS}
          />
          <span>
            <input
              name="scale"
              type="range"
              onChange={this.handleScale}
              min={this.state.allowZoomOut ? '0.1' : '1'}
              max="2"
              step="0.01"
              defaultValue="1"
            />
          </span>
          <IconV2
            className="avatar-upload__icon avatar-upload__icon--add"
            type={ICON_TYPES.PLUS}
          />
        </div>
        {this.state.image && (
          <div className="avatar-upload__buttons-container">
            <Button
              type={TYPES.SECONDARY}
              className="avatar-upload__button-save"
              onClick={(e) => this.onClickClose(e)}
              stacked
            >
              {staticText.getIn(['data', 'cancel'])}
            </Button>
            <Button
              type={TYPES.PRIMARY}
              className="avatar-upload__button-save"
              onClick={this.handleSave}
              stacked
            >
              {staticText.getIn(['data', 'save'])}
            </Button>
          </div>
        )}
      </div>
    )
  }
}

AvatarUpload.propTypes = {
  auth: ImmutablePropTypes.map.isRequired,
  user: ImmutablePropTypes.map.isRequired,
  communityProfile: ImmutablePropTypes.map.isRequired,
  setOverlayDialogVisible: PropTypes.func.isRequired,
  uploadAvatar: PropTypes.func.isRequired,
  updateUserProfileImages: PropTypes.func.isRequired,
  clearUserProfileData: PropTypes.func.isRequired,
}

export default compose(
  connectRedux(
    (state) => ({
      auth: state.auth,
      user: state.user,
      communityProfile: state.communityProfile,
    }),
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        setOverlayDialogVisible: actions.dialog.setOverlayDialogVisible,
        uploadAvatar: actions.communityProfile.uploadAvatar,
        updateUserProfileImages: actions.user.updateUserProfileImages,
        clearUserProfileData: actions.user.clearUserProfileData,
      }
    },
  ),
  connectStaticText({ storeKey: 'avatarUpload' }),
)(AvatarUpload)
